import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../lib/api';
import { useWebSocket } from '../hooks/useWebSocket';
import { useWebRTC } from '../hooks/useWebRTC';
import { filesize } from 'filesize';

export const ReceiverView = ({ sessionId, sessionData: propSessionData }) => {
  const [sessionData, setSessionData] = useState(propSessionData);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState({});
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [notifications, setNotifications] = useState([]);
  const [receivedFiles, setReceivedFiles] = useState({});

  useEffect(() => {
    const handleStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', handleStatus);
    window.addEventListener('offline', handleStatus);
    return () => {
      window.removeEventListener('online', handleStatus);
      window.removeEventListener('offline', handleStatus);
    };
  }, []);

  const onP2pFileReceived = useCallback((fileName, blob, fileId) => {
    console.log(`P2P file received: ${fileName} (${blob.size} bytes)`);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    setReceivedFiles(prev => ({ ...prev, [fileId]: true }));
    setNotifications(prev => [...prev, { id: fileId, text: `Downloaded: ${fileName}`, time: Date.now() }]);
    setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== fileId)), 5000);
  }, []);

  const incomingChunks = useRef({});
  const webrtcHandlersRef = useRef({});
  const requestedFilesRef = useRef(new Set());
  const [streamProgress, setStreamProgress] = useState({});
  const [pausedStreams, setPausedStreams] = useState({});
  const pausedStreamsRef = useRef({});

  const { isConnected, clientInfo, sendMessage } = useWebSocket(sessionId, async (message) => {
    if (message.type === 'broadcast_file_chunk') {
      const { fileId, chunk, isLast, fileName, fileType, fileSize, chunkIndex, totalChunks } = message;

      if (receivedFiles[fileId]) return;
      if (pausedStreamsRef.current[fileId]) return;

      // Decode base64 chunk to Uint8Array immediately for memory efficiency
      const byteString = atob(chunk.split(',')[1]);
      const uint8Array = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) {
        uint8Array[i] = byteString.charCodeAt(i);
      }

      // Initialize stream state for this file
      if (!incomingChunks.current[fileId]) {
        let streamState;
        // Use File System Access API for large files (Chrome/Edge only)
        if (window.showSaveFilePicker && fileSize > 100 * 1024 * 1024) {
          try {
            const extParts = fileName.split('.');
            const ext = extParts.length > 1 ? '.' + extParts.pop() : '';
            const handle = await window.showSaveFilePicker({
              suggestedName: fileName,
            });
            const writable = await handle.createWritable();
            streamState = { writable, received: 0, total: fileSize, useFS: true, chunksCount: 0 };
          } catch (err) {
            // User cancelled or API error — fall back to memory
            console.warn('File System Access API unavailable or user cancelled, using memory fallback');
            streamState = { chunks: [], received: 0, total: fileSize, useFS: false, chunksCount: 0 };
          }
        } else {
          // Memory fallback for small files or unsupported browsers
          if (fileSize > 500 * 1024 * 1024) {
            console.warn(`Large file (${(fileSize / (1024*1024*1024)).toFixed(1)}GB) in memory mode. May cause browser issues.`);
          }
          streamState = { chunks: [], received: 0, total: fileSize, useFS: false, chunksCount: 0 };
        }
        incomingChunks.current[fileId] = streamState;
      }

      const state = incomingChunks.current[fileId];

      if (state.useFS) {
        // Stream directly to disk — zero memory overhead
        await state.writable.write(uint8Array);
      } else {
        // Memory fallback
        state.chunks[chunkIndex] = uint8Array;
      }
      state.received += uint8Array.length;
      state.chunksCount++;

      // Send ack back to sender for backpressure flow control
      if (chunkIndex % 50 === 0 || isLast) {
        sendMessage({
          type: 'file_chunk_ack',
          to: message.from,
          fileId: fileId,
          chunkIndex: chunkIndex
        });
      }

      // Update progress
      const currentPercent = Math.round((state.chunksCount / totalChunks) * 100);
      setStreamProgress(prev => ({
        ...prev,
        [fileId]: {
          progress: Math.min(100, currentPercent),
          name: fileName,
          size: fileSize,
          chunksReceived: state.chunksCount,
          totalChunks
        }
      }));

      if (isLast) {
        if (state.useFS) {
          // File was streamed directly to disk
          try {
            await state.writable.close();
          } catch (e) {
            console.error('Error closing writable stream:', e);
          }
          setReceivedFiles(prev => ({ ...prev, [fileId]: true }));
          setNotifications(prev => [...prev, { id: fileId, text: `Saved: ${fileName}`, time: Date.now() }]);
          setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== fileId)), 5000);
        } else {
          // Memory fallback — create Blob and trigger download
          const chunks = state.chunks;
          const blob = new Blob(chunks.filter(Boolean), { type: fileType });
          const fileUrl = URL.createObjectURL(blob);

          const a = document.createElement('a');
          a.href = fileUrl;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(fileUrl), 10000);

          setReceivedFiles(prev => ({ ...prev, [fileId]: true }));
          setNotifications(prev => [...prev, { id: fileId, text: `Downloaded: ${fileName}`, time: Date.now() }]);
          setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== fileId)), 5000);

          setFiles(prev => {
            const existing = prev.find(f => f.id === fileId);
            const newFile = {
              id: fileId,
              name: fileName,
              size: fileSize,
              type: fileType,
              uploadedAt: Date.now(),
              localUrl: fileUrl,
              isStreamed: true
            };

            if (existing) {
              return prev.map(f => f.id === fileId ? { ...f, ...newFile } : f);
            }
            return [newFile, ...prev];
          });
        }

        setStreamProgress(prev => {
          const newState = { ...prev };
          delete newState[fileId];
          return newState;
        });
        delete incomingChunks.current[fileId];
      }
    } else if (message.type === 'file_announced') {
      if (message.file) {
        setFiles(prev => {
          if (prev.find(f => f.id === message.file.id)) return prev;
          return [message.file, ...prev];
        });
      }
    } else if (message.type === 'files_updated') {
      if (message.files) {
        setFiles(prev => {
          return message.files.map(newFile => {
            const existingFile = prev.find(f => f.id === newFile.id);
            if (existingFile) {
              return { ...existingFile, ...newFile, localUrl: existingFile.localUrl, isStreamed: existingFile.isStreamed };
            }
            return newFile;
          });
        });
      }
    } else if (message.type === 'session_closed') {
      setError('Session has been closed by the sender');
    } else if (message.type === 'cancel_file_transfer') {
      const { fileId } = message;
      setStreamProgress(prev => {
        const newState = { ...prev };
        delete newState[fileId];
        return newState;
      });
      setDownloading(prev => {
        const newState = { ...prev };
        delete newState[fileId];
        return newState;
      });
      delete incomingChunks.current[fileId];
    } else if (message.type === 'webrtc_offer') {
      console.log('WebRTC: Offer received');
      webrtcHandlersRef.current.handleOffer?.(message);
    } else if (message.type === 'webrtc_ice_candidate') {
      webrtcHandlersRef.current.handleIceCandidate?.(message);
    } else if (message.type === 'announce_file') {
      console.log('WebRTC: File announced:', message.fileName);
      webrtcHandlersRef.current.handleAnnounceFile?.(message);
    }
  });

  const { p2pFiles, p2pProgress, handleOffer, handleIceCandidate, handleAnnounceFile } = useWebRTC({
    sendMessage,
    onFileReceived: onP2pFileReceived,
  });

  useEffect(() => {
    webrtcHandlersRef.current = { handleOffer, handleIceCandidate, handleAnnounceFile };
  }, [handleOffer, handleIceCandidate, handleAnnounceFile]);

  // Prompt Flutter sender to open a WebRTC connection when we join
  useEffect(() => {
    if (isConnected && clientInfo?.clientId) {
      sendMessage({ type: 'p2p_hello', role: 'receiver' });
    }
  }, [isConnected, clientInfo?.clientId, sendMessage]);

  // Merge P2P-announced files into the display list immediately
  useEffect(() => {
    if (p2pFiles.length === 0) return;
    setFiles(prev => {
      const existingIds = new Set(prev.map(f => f.id));
      const merged = [...prev];
      for (const pf of p2pFiles) {
        if (!existingIds.has(pf.id)) {
          merged.push({
            id: pf.id,
            name: pf.name,
            size: pf.size,
            type: pf.type || '',
            isP2P: true,
          });
          existingIds.add(pf.id);
        }
      }
      return merged;
    });
  }, [p2pFiles]);

  useEffect(() => {
    if (sessionId) {
      loadSessionFiles();
    }
  }, [sessionId]);

  useEffect(() => {
    // Auto-refresh files every 10 seconds if no WebSocket connection
    if (!isConnected && sessionId) {
      const interval = setInterval(() => {
        loadSessionFiles();
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [isConnected, sessionId, lastRefresh]);

  useEffect(() => {
    // Initial files list from 'connected' message — very helpful for late joiners!
    if (clientInfo?.sessionInfo?.files) {
      setFiles(prev => {
        return clientInfo.sessionInfo.files.map(newFile => {
          const existingFile = prev.find(f => f.id === newFile.id);
          if (existingFile) {
            // Server/session data wins over stale socket-only placeholders
            return { ...existingFile, ...newFile };
          }
          return newFile;
        });
      });
    }
  }, [clientInfo]);

  // Clear requested files when disconnected to allow requesting again on reconnection
  useEffect(() => {
    if (!isConnected) {
      requestedFilesRef.current.clear();
    }
  }, [isConnected]);

  // Auto-request files that we don't have yet (very helpful for late joiners)
  useEffect(() => {
    if (!isConnected) return;
    files.forEach(file => {
      if (file.isSocketFile && !file.localUrl && !streamProgress[file.id] && !downloading[file.id] && !receivedFiles[file.id]) {
        if (!requestedFilesRef.current.has(file.id)) {
          const ownerId = file.ownerId;
          if (ownerId) {
            requestedFilesRef.current.add(file.id);
            console.log(`Auto-requesting stream/file: ${file.name} from owner ${ownerId}`);
            sendMessage({
              type: 'request_file',
              to: ownerId,
              fileId: file.id,
              fileName: file.name,
              fileSize: file.size
            });
          }
        }
      }
    });
  }, [files, isConnected, streamProgress, downloading, receivedFiles, sendMessage]);

  const handleManualRefresh = () => {
    setLastRefresh(Date.now());
    loadSessionFiles();
  };

  const loadSessionFiles = async () => {
    if (!sessionId) return;

    setIsLoading(true);
    setError('');

    try {
      const response = await api.getSessionFiles(sessionId);

      if (response.error) {
        setError(response.error);
        return;
      }

      setFiles(prev => {
        const serverFiles = response.files || [];
        const existingIds = new Set(serverFiles.map(f => f.id));
        const p2pToKeep = prev.filter(f => f.isP2P && !existingIds.has(f.id));
        
        const mergedServerFiles = serverFiles.map(sf => {
          const pf = prev.find(f => f.id === sf.id);
          if (pf) {
            return {
              ...pf,
              ...sf,
              localUrl: pf.localUrl || sf.localUrl,
              isStreamed: pf.isStreamed || sf.isStreamed,
              isSocketFile: pf.isSocketFile || sf.isSocketFile,
            };
          }
          return sf;
        });

        return [...mergedServerFiles, ...p2pToKeep];
      });
    } catch (error) {
      const errorMessage = error.message || 'Failed to load files';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = (fileId, fileName) => {
    if (!sessionId) return;

    // Mark as downloading
    setDownloading(prev => ({ ...prev, [fileId]: true }));

    // Create download link
    const file = files.find(f => f.id === fileId);
    let downloadUrl;

    // If it's a streamed file, we use the local Blob URL we generated earlier
    if (file && file.localUrl) {
      downloadUrl = file.localUrl;
    } else {
      const clientId = clientInfo?.clientId || 'unknown_client';
      downloadUrl = `${api.downloadFile(sessionId, fileId)}?clientId=${clientId}`;
    }

    const downloadLink = document.createElement('a');
    downloadLink.href = downloadUrl;
    downloadLink.setAttribute('download', fileName || file?.originalName || 'file');
    document.body.appendChild(downloadLink);
    downloadLink.click();

    setReceivedFiles(prev => ({ ...prev, [fileId]: true }));

    // Small delay before cleanup and state reset
    setTimeout(() => {
      document.body.removeChild(downloadLink);
      setDownloading(prev => {
        const newDownloading = { ...prev };
        delete newDownloading[fileId];
        return newDownloading;
      });
    }, 1000);
  };

  const downloadableServerFiles = files.filter(f => !f.isSocketFile);

  const toggleStreamPause = (file) => {
    const isPaused = pausedStreamsRef.current[file.id];
    const nextPaused = !isPaused;
    pausedStreamsRef.current = { ...pausedStreamsRef.current, [file.id]: nextPaused };
    setPausedStreams(prev => ({ ...prev, [file.id]: nextPaused }));
    if (file.ownerId) {
      sendMessage({
        type: nextPaused ? 'transfer_pause' : 'transfer_resume',
        to: file.ownerId,
        fileId: file.id,
      });
    }
  };

  const handleDownloadAll = () => {
    if (!sessionId || downloadableServerFiles.length === 0) return;

    setIsDownloadingAll(true);

    const downloadUrl = api.downloadAll(sessionId);

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', `${sessionData?.senderName || 'heksta'}-files.zip`);
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      setIsDownloadingAll(false);
    }, 2000);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    return filesize(bytes, { base: 2, standard: 'jedec' });
  };

  const getFileIcon = (mimeType) => {
    if (!mimeType) return '📄';

    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType.startsWith('video/')) return '🎬';
    if (mimeType.startsWith('audio/')) return '🎵';
    if (mimeType.includes('pdf')) return '📚';
    if (mimeType.includes('zip') || mimeType.includes('compressed')) return '📦';
    if (mimeType.includes('text')) return '📝';

    return '📄';
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--primary)] mb-4"></div>
          <p className="text-gray-600">Loading files...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="theme-orange">
      <div className="max-w-3xl mx-auto px-4 py-8 animate-fade-in-up">
      {/* Session Header */}
      <div className="card p-5 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-lg font-bold mb-3 truncate" style={{color: 'var(--text-primary)'}}>
              Files from {sessionData?.senderName || 'Sender'}
            </h1>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-1.5">
                <span className={`${isConnected ? 'status-dot-online' : (isOnline ? 'status-dot-connecting' : 'status-dot-offline')}`}></span>
                <span className={`text-xs font-semibold ${isConnected ? 'text-emerald-600 dark:text-emerald-400' : (isOnline ? 'text-amber-600 dark:text-amber-400' : 'text-red-500 dark:text-red-400')}`}>
                  {isConnected ? 'LIVE' : (isOnline ? 'RECONNECTING' : 'OFFLINE')}
                </span>
              </div>
              <span className="badge-outline font-mono text-xs">{sessionId}</span>
              {clientInfo?.sessionInfo?.connectedClients !== undefined && (
                <span className="text-xs" style={{color: 'var(--text-muted)'}}>{clientInfo.sessionInfo.connectedClients} online</span>
              )}
            </div>
          </div>
          <button onClick={handleManualRefresh} disabled={isLoading} className="btn-secondary text-xs py-2 px-3 flex-shrink-0">
            {isLoading ? 'Refreshing...' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="card mb-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">⚠️ {error}</p>
          </div>
        </div>
      )}

      {/* P2P Download Notifications */}
      {notifications.length > 0 && (
        <div className="fixed top-24 right-4 z-50 space-y-2">
          {notifications.map(n => (
            <div key={n.id} className="bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg text-sm font-medium">
              ✓ {n.text}
            </div>
          ))}
        </div>
      )}

      {/* Files List */}
      {files.length === 0 ? (
        <div className="card text-center py-14">
          <div className="text-4xl mb-4">📁</div>
          <h3 className="text-base font-bold mb-2" style={{color: 'var(--text-primary)'}}>No files yet</h3>
          <p className="text-sm mb-5" style={{color: 'var(--text-secondary)'}}>
            {isConnected ? 'Waiting for the sender to upload files...' : 'Connection lost. Please refresh.'}
          </p>
          <div className="flex gap-3 justify-center">
            <button onClick={handleManualRefresh} disabled={isLoading} className="btn-primary text-sm py-2.5 px-5">
              {isLoading ? 'Checking...' : 'Check for Files'}
            </button>
            {!isConnected && (
              <button onClick={() => window.location.reload()} className="btn-secondary text-sm py-2.5">Reload</button>
            )}
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold" style={{color: 'var(--text-primary)'}}>
              Available Files ({files.length})
            </h3>
            <div className="flex items-center gap-4">
              <div className="text-sm hidden sm:block" style={{color: 'var(--text-muted)'}}>
                Total size: {formatFileSize(files.reduce((sum, file) => sum + (file.size || 0), 0))}
              </div>
              {downloadableServerFiles.length > 1 && (
                <button
                  onClick={handleDownloadAll}
                  disabled={isDownloadingAll}
                  className={`btn-primary flex items-center gap-2 py-2 px-4 ${isDownloadingAll ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isDownloadingAll ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Zipping...
                    </>
                  ) : (
                    <>
                      <span>📦</span>
                      Download All
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Incoming Streams Section */}
          {Object.keys(streamProgress).length > 0 && (
            <div className="mb-8 space-y-4">
              <h4 className="text-sm font-bold text-[var(--primary)] uppercase tracking-widest flex items-center gap-2">
                <span className="w-2 h-2 bg-[var(--primary)] rounded-full animate-pulse"></span>
                Incoming Live Stream
              </h4>
              {Object.entries(streamProgress).map(([fileId, info]) => (
                <div key={fileId} className="p-4 rounded-xl relative overflow-hidden" style={{backgroundColor: 'var(--primary-50)', border: '1px solid var(--primary-100)'}}>
                  <div className="flex items-center justify-between mb-2 relative z-10">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">📡</span>
                      <div>
                        <p className="text-sm font-bold truncate max-w-[200px] sm:max-w-md" style={{color: 'var(--text-primary)'}}>{info.name}</p>
                        <p className="text-[10px] font-bold uppercase" style={{color: 'var(--primary)'}}>{formatFileSize(info.size)} · RECEIVING</p>
                      </div>
                    </div>
                    <span className="text-sm font-black" style={{color: 'var(--primary)'}}>{info.progress}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-[var(--primary-accent)]/20 rounded-full overflow-hidden">
                    <div className="h-full bg-[var(--primary)] transition-all duration-300" style={{ width: `${info.progress}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-3">
            {files.map((file) => {
              const isP2P = file.isP2P;
              const p2pProgressVal = p2pProgress[file.id];
              const isDownloadingP2P = p2pProgressVal !== undefined && p2pProgressVal < 100;

              return (
              <div
                key={file.id}
                className="file-item"
              >
                <div className="flex items-center flex-1 min-w-0">
                  <div className="text-2xl mr-4">
                    {isP2P ? '⚡' : getFileIcon(file.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium truncate" style={{color: 'var(--text-primary)'}}>
                      {file.name}
                      {isP2P && <span className="ml-2 text-xs text-green-600 font-semibold">P2P</span>}
                    </h4>
                    <div className="flex items-center gap-4 text-sm mt-1" style={{color: 'var(--text-secondary)'}}>
                      <span>{formatFileSize(file.size)}</span>
                      {file.type && !isP2P && (
                        <span className="truncate">{file.type}</span>
                      )}
                    </div>
                    {isDownloadingP2P && (
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full transition-all duration-300" style={{ width: `${p2pProgressVal}%` }} />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {receivedFiles[file.id] ? (
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-md border border-green-200">
                        ✓ Received
                      </span>
                      {(file.localUrl || !file.isSocketFile) && (
                        <button
                          onClick={() => handleDownload(file.id, file.name)}
                          disabled={downloading[file.id]}
                          className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-bold rounded-md border border-slate-200 dark:border-slate-700 transition-colors"
                        >
                          Save Again
                        </button>
                      )}
                    </div>
                  ) : isDownloadingP2P ? (
                    <span className="px-3 py-1 bg-green-50 text-green-600 text-xs font-bold rounded-md">
                      {p2pProgressVal}%
                    </span>
                  ) : isP2P ? (
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-md border border-green-200">
                      ✓ Received
                    </span>
                  ) : (
                    <>
                  {file.isSocketFile && !file.localUrl && !streamProgress[file.id] && (
                    <span className="px-3 py-1 bg-amber-50 text-amber-600 text-[10px] font-black rounded-md border border-amber-100 uppercase tracking-widest">
                      Waiting for stream...
                    </span>
                  )}
                  {streamProgress[file.id] && (
                    <>
                      <span className="px-3 py-1 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 text-[10px] font-black rounded-md border border-emerald-100 dark:border-emerald-800/40 uppercase tracking-widest animate-pulse">
                        {pausedStreams[file.id] ? 'Paused' : `Receiving (${streamProgress[file.id].progress}%)`}
                      </span>
                      <button
                        onClick={() => toggleStreamPause(file)}
                        className="px-2 py-1 text-xs rounded-md border border-gray-200"
                        title={pausedStreams[file.id] ? 'Resume' : 'Pause'}
                      >
                        {pausedStreams[file.id] ? '▶' : '⏸'}
                      </button>
                    </>
                  )}

                  {(file.localUrl || !file.isSocketFile) && (
                    <button
                      onClick={() => handleDownload(file.id, file.name)}
                      disabled={downloading[file.id]}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${downloading[file.id]
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : file.isStreamed ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-primary-600 hover:bg-primary-700 text-white'
                        }`}
                    >
                      {downloading[file.id] ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Downloading...
                        </div>
                      ) : (
                        file.isStreamed ? 'Save File' : 'Download'
                      )}
                    </button>
                  )}
                  </>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tip */}
      <p className="mt-6 text-center text-xs" style={{color: 'var(--text-muted)'}}>
        💡 Click download next to any file. Large files stream at full network speed.
      </p>
      </div>
    </div>
  );
};